import React from 'react';
import { Sparkles, Target, CheckCircle2, FlaskConical } from 'lucide-react';

/**
 * Single, prominent "Benefits & Target Organs" section that lives next to
 * the main product image. Pulls multilingual content from
 * product.target_organs_meta[lang] populated by the backend
 * peptide_profiles.py knowledge map.
 *
 * Falls back gracefully to product.benefits[] + product.organ_images[] when
 * the enriched metadata is missing, so the layout never breaks for a
 * product that hasn't been profiled yet.
 */
const BenefitsTargetOrgans = ({ product, currentLanguage = 'en' }) => {
  if (!product) return null;

  const meta = product.target_organs_meta?.[currentLanguage]
    || product.target_organs_meta?.en
    || null;

  const fallbackBenefits = product.benefits || [];
  const benefits = (meta?.benefits && meta.benefits.length > 0)
    ? meta.benefits
    : fallbackBenefits;

  const targetOrgansText = meta?.target_organs;
  const summaryText = meta?.summary;
  const organImages = product.organ_images || [];

  if (!targetOrgansText && !summaryText && benefits.length === 0 && organImages.length === 0) {
    return null;
  }

  const labels = {
    en: {
      heading: 'Benefits & Target Organs',
      kicker: 'Research Use Profile',
      organsLabel: 'Target Organs & Systems',
      summaryLabel: 'Research Summary',
      benefitsLabel: 'Key Research Applications',
      ruo: 'Research Use Only, not for human or veterinary consumption.',
    },
    es: {
      heading: 'Beneficios y Órganos Diana',
      kicker: 'Perfil de Investigación',
      organsLabel: 'Órganos y Sistemas Diana',
      summaryLabel: 'Resumen de Investigación',
      benefitsLabel: 'Aplicaciones de Investigación Clave',
      ruo: 'Uso exclusivo de investigación, no apto para consumo humano o veterinario.',
    },
    ru: {
      heading: 'Преимущества и целевые органы',
      kicker: 'Профиль исследовательского применения',
      organsLabel: 'Целевые органы и системы',
      summaryLabel: 'Резюме исследований',
      benefitsLabel: 'Ключевые исследовательские применения',
      ruo: 'Только для исследовательского использования, не для потребления человеком или животными.',
    },
    de: {
      heading: 'Nutzen & Zielorgane',
      kicker: 'Forschungsanwendungsprofil',
      organsLabel: 'Zielorgane & Systeme',
      summaryLabel: 'Forschungszusammenfassung',
      benefitsLabel: 'Wichtige Forschungsanwendungen',
      ruo: 'Nur zu Forschungszwecken, nicht für den menschlichen oder tierischen Verzehr bestimmt.',
    },
    fr: {
      heading: 'Bénéfices & Organes Cibles',
      kicker: 'Profil d’utilisation en recherche',
      organsLabel: 'Organes & Systèmes cibles',
      summaryLabel: 'Résumé de recherche',
      benefitsLabel: 'Applications clés en recherche',
      ruo: 'Usage de recherche uniquement, non destiné à la consommation humaine ou vétérinaire.',
    },
    it: {
      heading: 'Benefici & Organi Bersaglio',
      kicker: 'Profilo di Uso in Ricerca',
      organsLabel: 'Organi & Sistemi Bersaglio',
      summaryLabel: 'Riepilogo della Ricerca',
      benefitsLabel: 'Applicazioni Chiave di Ricerca',
      ruo: 'Solo per uso di ricerca, non per consumo umano o veterinario.',
    },
    pl: {
      heading: 'Korzyści i Narządy Docelowe',
      kicker: 'Profil Zastosowania Badawczego',
      organsLabel: 'Narządy i Układy Docelowe',
      summaryLabel: 'Podsumowanie Badań',
      benefitsLabel: 'Kluczowe Zastosowania Badawcze',
      ruo: 'Wyłącznie do celów badawczych, nie do spożycia przez ludzi ani zwierzęta.',
    },
    nl: {
      heading: 'Voordelen & Doelorganen',
      kicker: 'Onderzoeksprofiel',
      organsLabel: 'Doelorganen & Systemen',
      summaryLabel: 'Onderzoekssamenvatting',
      benefitsLabel: 'Belangrijkste Onderzoekstoepassingen',
      ruo: 'Alleen voor onderzoeksdoeleinden, niet voor menselijke of veterinaire consumptie.',
    },
    pt: {
      heading: 'Benefícios & Órgãos-Alvo',
      kicker: 'Perfil de Uso em Pesquisa',
      organsLabel: 'Órgãos & Sistemas-Alvo',
      summaryLabel: 'Resumo da Pesquisa',
      benefitsLabel: 'Principais Aplicações de Pesquisa',
      ruo: 'Apenas para uso em pesquisa, não para consumo humano ou veterinário.',
    },
  }[currentLanguage] || {
      heading: 'Benefits & Target Organs',
      kicker: 'Research Use Profile',
      organsLabel: 'Target Organs & Systems',
      summaryLabel: 'Research Summary',
      benefitsLabel: 'Key Research Applications',
      ruo: 'Research Use Only, not for human or veterinary consumption.',
  };

  return (
    <div
      className="glass-card rounded-3xl p-6 md:p-7 border-l-4 border-capsule-teal shadow-sm"
      data-testid="benefits-organs-section"
    >
      {/* Header */}
      <div className="flex items-start gap-3 mb-5">
        <div className="w-11 h-11 bg-gradient-to-br from-capsule-teal to-capsule-aqua rounded-xl flex items-center justify-center flex-shrink-0">
          <Sparkles className="w-5 h-5 text-white" />
        </div>
        <div className="min-w-0">
          <p className="text-[11px] uppercase tracking-[0.18em] text-capsule-teal font-bold">
            {labels.kicker}
          </p>
          <h2 className="text-lg md:text-xl font-bold text-gray-900 leading-tight">
            {labels.heading}
          </h2>
        </div>
      </div>

      {/* Organ image (large + visible) */}
      {organImages.length > 0 && (
        <div className="mb-5 -mx-2 sm:mx-0">
          <div className="rounded-2xl overflow-hidden bg-gradient-to-br from-slate-50 to-white border border-capsule-teal/15 p-2">
            <img
              src={organImages[0]}
              alt={`${product.name?.[currentLanguage] || product.name?.en || ''}, target organ research illustration`}
              loading="lazy"
              className="w-full h-44 sm:h-52 object-contain"
              data-testid="organ-image-primary"
            />
          </div>
          {organImages.length > 1 && (
            <div className="flex gap-2 mt-2 justify-center flex-wrap">
              {organImages.slice(1, 5).map((src, i) => (
                <div
                  key={i}
                  className="w-14 h-14 rounded-lg overflow-hidden border border-capsule-teal/20 bg-white"
                  data-testid={`organ-image-thumb-${i}`}
                >
                  <img src={src} alt={`${product.name?.[currentLanguage] || product.name?.en || ''} target organ illustration`} loading="lazy" className="w-full h-full object-contain" />
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Target Organs textual line */}
      {targetOrgansText && (
        <div className="mb-4 p-3 bg-capsule-teal/[0.06] rounded-xl border border-capsule-teal/15">
          <div className="flex items-start gap-2">
            <Target className="w-4 h-4 text-capsule-teal flex-shrink-0 mt-0.5" />
            <div className="min-w-0">
              <p className="text-[11px] uppercase tracking-wider text-capsule-teal font-bold mb-1">
                {labels.organsLabel}
              </p>
              <p
                className="text-sm text-gray-800 leading-relaxed"
                data-testid="target-organs-text"
              >
                {targetOrgansText}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Research Summary paragraph */}
      {summaryText && (
        <div className="mb-4">
          <div className="flex items-start gap-2">
            <FlaskConical className="w-4 h-4 text-capsule-teal flex-shrink-0 mt-0.5" />
            <div className="min-w-0">
              <p className="text-[11px] uppercase tracking-wider text-capsule-teal font-bold mb-1">
                {labels.summaryLabel}
              </p>
              <p
                className="text-sm text-gray-700 leading-relaxed"
                data-testid="research-summary"
              >
                {summaryText}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Benefits bullets */}
      {benefits.length > 0 && (
        <div>
          <p className="text-[11px] uppercase tracking-wider text-capsule-teal font-bold mb-2">
            {labels.benefitsLabel}
          </p>
          <ul className="space-y-1.5">
            {benefits.map((b, i) => (
              <li
                key={i}
                className="flex items-start gap-2 text-sm text-gray-800"
                data-testid={`benefit-item-${i}`}
              >
                <CheckCircle2 className="w-4 h-4 text-capsule-teal flex-shrink-0 mt-0.5" />
                <span className="leading-snug">{b}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* RUO footer */}
      <div className="mt-5 pt-3 border-t border-gray-200/70 text-[11px] text-gray-500 italic">
        {labels.ruo}
      </div>
    </div>
  );
};

export default BenefitsTargetOrgans;
